import type { Aabb } from '@/combat/swing';
import { ComponentStore, EntityWorld } from '@/ecs/world';
import type { Facing } from '@/sprites/katie';
import type { Tilemap } from '@/world/tilemap';

/**
 * Spirit bolts in flight.
 *
 * The second thing to live in the entity world, and the reason it was worth
 * building: a bolt is a position, a velocity and a lifetime, sharing nothing
 * with an enemy but the store it lives in.
 *
 * A bolt is spent on whatever it hits first. Piercing shots are a weapon
 * property to add later, not the default — a projectile that mows down a line
 * makes the melee weapon pointless the moment it exists.
 */

export const BOLT_SPEED = 132;
export const BOLT_LIFETIME = 1.1;
export const BOLT_SIZE = 6;

const DIRECTIONS: Readonly<Record<Facing, { x: number; y: number }>> = {
  down: { x: 0, y: 1 },
  up: { x: 0, y: -1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

export interface Bolt {
  x: number;
  y: number;
  readonly dx: number;
  readonly dy: number;
  readonly damage: number;
  life: number;
}

export class Projectiles {
  private readonly world = new EntityWorld();
  private readonly bolts: ComponentStore<Bolt>;

  constructor() {
    this.bolts = this.world.register(new ComponentStore<Bolt>());
  }

  get count(): number {
    return this.bolts.size;
  }

  all(): Bolt[] {
    return this.bolts.values();
  }

  fire(x: number, y: number, facing: Facing, damage: number): void {
    const direction = DIRECTIONS[facing];
    const entity = this.world.create();
    this.bolts.set(entity, {
      x,
      y,
      dx: direction.x * BOLT_SPEED,
      dy: direction.y * BOLT_SPEED,
      damage,
      life: BOLT_LIFETIME,
    });
  }

  clear(): void {
    this.world.clear();
  }

  box(bolt: Bolt): Aabb {
    return {
      x: bolt.x - BOLT_SIZE / 2,
      y: bolt.y - BOLT_SIZE / 2,
      width: BOLT_SIZE,
      height: BOLT_SIZE,
    };
  }

  /**
   * Fly, expire, and stop at walls.
   *
   * `onHit` is asked about each bolt's new position and returns true when it
   * struck something; the bolt is spent either way it stops. Keeping the
   * enemy lookup outside means projectiles know nothing about what they hurt,
   * which is what lets an enemy fire one back later.
   */
  update(dt: number, map: Tilemap, onHit: (box: Aabb, damage: number) => boolean): void {
    for (const [entity, bolt] of this.bolts.entries()) {
      bolt.life -= dt;
      bolt.x += bolt.dx * dt;
      bolt.y += bolt.dy * dt;

      const box = this.box(bolt);

      if (bolt.life <= 0 || map.collides(box)) {
        this.world.destroy(entity);
        continue;
      }

      if (onHit(box, bolt.damage)) this.world.destroy(entity);
    }
  }
}
